import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronRight,
  FiClipboard,
  FiDollarSign,
  FiFileText,
  FiPackage,
  FiSearch,
  FiTruck,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import Header from "./Header";
import Sidebar from "../Sidebar/Sidebar";
import { globalSearch } from "../../services/header_service";
import "./GlobalSearch.css";

const API_URL =
  process.env.REACT_APP_API_URL || "https://srinivasafleet.com/api";

const API_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const SECTION_CONFIG = {
  vehicle: {
    title: "Vehicles",
    singular: "Vehicle",
    className: "vehicle",
    icon: FiTruck,
  },
  driver: {
    title: "Drivers",
    singular: "Driver",
    className: "driver",
    icon: FiUsers,
  },
  supplier: {
    title: "Suppliers",
    singular: "Supplier",
    className: "supplier",
    icon: FiPackage,
  },
  assignment: {
    title: "Allotments",
    singular: "Allotment",
    className: "assignment",
    icon: FiClipboard,
  },
  staff: {
    title: "Staff",
    singular: "Staff",
    className: "staff",
    icon: FiUser,
  },
  outstanding: {
    title: "Outstanding",
    singular: "Outstanding",
    className: "outstanding",
    icon: FiDollarSign,
  },
  vehicle_documents: {
    title: "Vehicle Documents",
    singular: "Vehicle Document",
    className: "documents",
    icon: FiFileText,
  },
};

const SECTION_ORDER = [
  "vehicle",
  "driver",
  "supplier",
  "assignment",
  "staff",
  "outstanding",
  "vehicle_documents",
];

function getSource(item) {
  if (
    item?.details &&
    typeof item.details === "object" &&
    !Array.isArray(item.details)
  ) {
    return {
      ...item,
      ...item.details,
    };
  }

  return item || {};
}

function normalizeResults(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];

  const grouped = {
    vehicle: [],
    driver: [],
    supplier: [],
    assignment: [],
    staff: [],
    outstanding: [],
    vehicle_documents: [],
  };

  raw.forEach((item) => {
    const type = String(item?.type || "").toLowerCase();

    if (type === "vehicle") {
      grouped.vehicle.push(item);
    } else if (type === "driver") {
      grouped.driver.push(item);
    } else if (type === "supplier") {
      grouped.supplier.push(item);
    } else if (type === "assignment" || type === "allotment") {
      grouped.assignment.push(item);
    } else if (type === "staff" || type === "user") {
      grouped.staff.push(item);
    } else if (type === "outstanding") {
      grouped.outstanding.push(item);
    } else if (
      type === "vehicle_document" ||
      type === "vehicle_documents" ||
      type === "document"
    ) {
      grouped.vehicle_documents.push(item);
    }
  });

  return grouped;
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(number);
}

function makeUrl(value) {
  if (!value) return null;

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${API_ORIGIN}${String(value).startsWith("/") ? value : `/${value}`}`;
}

function DetailItem({ label, value }) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }

  return (
    <div className="global-search-detail-item">
      <span>{label}</span>
      <strong>{String(value)}</strong>
    </div>
  );
}

function FileButton({ label, value }) {
  const url = makeUrl(value);

  if (!url) return null;

  return (
    <a
      className="global-search-file"
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      <FiFileText />
      <span>{label}</span>
    </a>
  );
}

function getDisplay(item, type) {
  const source = getSource(item);

  if (type === "vehicle") {
    return {
      title:
        source.registration_number ||
        source.vehicle_registration_number ||
        item?.title ||
        "Vehicle",
      subtitle: [
        source.display_number || source.vehicle_display_number,
        source.model || source.model_name,
        source.year_of_make,
      ]
        .filter(Boolean)
        .join(" • "),
      meta: source.vehicle_code || item?.meta || "",
      image:
        source.vehicle_photo ||
        source.vehicle_photo_url ||
        item?.image_url,
    };
  }

  if (type === "driver") {
    return {
      title:
        source.full_name ||
        item?.title ||
        "Driver",
      subtitle: [
        source.mobile,
        source.license_number,
      ]
        .filter(Boolean)
        .join(" • "),
      meta: source.driver_code || item?.meta || "",
      image:
        source.driver_photo_url ||
        source.driver_photo ||
        item?.image_url,
    };
  }

  if (type === "supplier") {
    return {
      title:
        source.supplier_name ||
        item?.title ||
        "Supplier",
      subtitle: [
        source.mobile,
        source.supplier_type,
        source.branch,
      ]
        .filter(Boolean)
        .join(" • "),
      meta: source.supplier_code || item?.meta || "",
      image:
        source.photo_url ||
        source.supplier_photo_url ||
        item?.image_url,
    };
  }

  if (type === "assignment") {
    return {
      title:
        source.unique_number ||
        source.transaction_id ||
        item?.title ||
        "Allotment",
      subtitle: [
        source.vehicle_number,
        source.supplier_name,
        source.driver_name,
        source.assignment_type,
      ]
        .filter(Boolean)
        .join(" • "),
      meta: source.transaction_id || item?.meta || "",
      image:
        source.vehicle_photo_front ||
        item?.image_url,
    };
  }

  if (type === "staff") {
    return {
      title:
        source.full_name ||
        item?.title ||
        "Staff",
      subtitle: [
        source.designation,
        source.mobile,
        source.role_name,
      ]
        .filter(Boolean)
        .join(" • "),
      meta: source.staff_code || item?.meta || "",
      image:
        source.photo_url ||
        source.avatar_url ||
        item?.image_url,
    };
  }

  if (type === "outstanding") {
    return {
      title:
        source.vehicle_number ||
        source.supplier_name ||
        item?.title ||
        "Outstanding",
      subtitle: [
        source.supplier_name,
        source.supplier_code,
        source.assignment_number,
      ]
        .filter(Boolean)
        .join(" • "),
      meta:
        source.outstanding_amount !== undefined
          ? formatCurrency(source.outstanding_amount)
          : item?.meta || "",
      image: item?.image_url,
    };
  }

  return {
    title:
      source.vehicle_number ||
      source.registration_number ||
      item?.title ||
      "Vehicle Documents",
    subtitle: [
      source.vehicle_model,
      source.display_code,
    ]
      .filter(Boolean)
      .join(" • "),
    meta: item?.meta || "Vehicle Documents",
    image: item?.image_url,
  };
}

function getDetails(type, item) {
  const s = getSource(item);

  if (type === "vehicle") {
    return [
      ["Registration Number", s.registration_number || s.vehicle_registration_number],
      ["Display Number", s.display_number || s.vehicle_display_number],
      ["Vehicle Code", s.vehicle_code],
      ["Model", s.model],
      ["Company", s.company],
      ["Year", s.year_of_make],
      ["Fuel Type", s.fuel_type],
      ["Transmission", s.transmission_type],
      ["Engine Number", s.engine_number],
      ["Chassis Number", s.chassis_number],
      ["Seating Capacity", s.seating_capacity],
      ["Registration Date", formatDate(s.registration_date)],
      ["Average Mileage", s.average_mileage],
      [
        "GPS",
        s.gps_enabled === true
          ? "Enabled"
          : s.gps_enabled === false
            ? "Disabled"
            : "",
      ],
      [
        "Status",
        s.is_active === true
          ? "Active"
          : s.is_active === false
            ? "Inactive"
            : "",
      ],
    ];
  }

  if (type === "driver") {
    return [
      ["Driver Code", s.driver_code],
      ["Full Name", s.full_name],
      ["Mobile", s.mobile],
      ["Email", s.email],
      ["License Number", s.license_number],
      ["License Expiry", formatDate(s.license_expiry)],
      ["Aadhaar Number", s.aadhaar_number],
      ["PAN", s.pancard_number],
      ["Date of Birth", formatDate(s.date_of_birth)],
      ["Joining Date", formatDate(s.joining_date)],
      ["Blood Group", s.blood_group],
      ["Character", s.character_nature],
      ["Supplier Type", s.supplier_type],
      ["Branch", s.branch],
      ["City", s.city],
      ["State", s.state],
      ["Pincode", s.pincode],
      ["Address", s.address],
      ["Emergency Contact", [
        s.emergency_contact_name,
        s.emergency_contact_number,
      ].filter(Boolean).join(" • ")],
      ["Reference Contact", [
        s.reference_person_name,
        s.reference_contact_number_1,
      ].filter(Boolean).join(" • ")],
      [
        "Status",
        s.is_active === true
          ? "Active"
          : s.is_active === false
            ? "Inactive"
            : "",
      ],
    ];
  }

  if (type === "supplier") {
    return [
      ["Supplier Code", s.supplier_code],
      ["Supplier Name", s.supplier_name],
      ["Mobile", s.mobile],
      ["Alternate Mobile", s.alternate_mobile],
      ["Email", s.email],
      ["Supplier Type", s.supplier_type],
      ["Branch", s.branch],
      ["Maximum Vehicle Capacity", s.max_vehicle_capacity],
      ["Aadhaar Number", s.aadhaar_number],
      ["PAN", s.pancard_number],
      ["License Number", s.license_number],
      ["License Expiry", formatDate(s.license_expiry)],
      ["City", s.city],
      ["State", s.state],
      ["Pincode", s.pincode],
      ["Permanent Address", s.permanent_address],
      ["Temporary Address", s.temporary_address],
      ["Current Address", s.current_address],
      ["Agreement Status", s.agreement_status],
      ["Agreement Start", formatDate(s.agreement_start_date)],
      ["Agreement End", formatDate(s.agreement_end_date)],
      [
        "Agreement Tenure",
        s.agreement_tenure_months
          ? `${s.agreement_tenure_months} months`
          : "",
      ],
      [
        "EMI Completed",
        s.emi_completed_months
          ? `${s.emi_completed_months} months`
          : "",
      ],
      ["Remarks", s.remarks],
      [
        "Status",
        s.is_active === true
          ? "Active"
          : s.is_active === false
            ? "Inactive"
            : "",
      ],
    ];
  }

  if (type === "assignment") {
    return [
      ["Allotment Number", s.unique_number],
      ["Transaction ID", s.transaction_id],
      ["Assigned Date", formatDate(s.assigned_date)],
      ["Transaction Date", formatDate(s.transaction_date)],
      ["Relieved Date", formatDate(s.relieved_date)],
      ["Vehicle Number", s.vehicle_number],
      ["Vehicle Display Number", s.vehicle_display_number],
      ["Vehicle Code", s.vehicle_code],
      ["Supplier", s.supplier_name],
      ["Supplier Code", s.supplier_code],
      ["Supplier Mobile", s.supplier_mobile],
      ["Driver", s.driver_name],
      ["Driver Code", s.driver_code],
      ["Driver Mobile", s.driver_mobile],
      ["Assignment Type", s.assignment_type],
      ["Branch", s.branch],
      ["Service Location", s.service_location],
      [
        "Odometer",
        s.vehicle_odometer_km !== undefined &&
        s.vehicle_odometer_km !== null
          ? `${s.vehicle_odometer_km} km`
          : "",
      ],
      ["Outstanding Amount", formatCurrency(s.outstanding_amount)],
      [
        "Status",
        s.is_active === true
          ? "Active"
          : s.is_active === false
            ? "Inactive"
            : "",
      ],
    ];
  }

  if (type === "staff") {
    return [
      ["Staff Code", s.staff_code],
      ["Name", s.full_name],
      ["Designation", s.designation],
      ["Role", s.role_name],
      ["Mobile", s.mobile],
      ["Email", s.email],
      ["Branch", s.branch],
      ["Service Location", s.service_location],
      ["City", s.city],
      ["Pincode", s.pincode],
      ["Address", s.address],
      [
        "Status",
        s.is_blocked
          ? "Blocked"
          : s.is_active
            ? "Active"
            : "Inactive",
      ],
    ];
  }

  if (type === "outstanding") {
    return [
      ["Vehicle Number", s.vehicle_number],
      ["Vehicle Display Number", s.vehicle_display_number],
      ["Vehicle Code", s.vehicle_code],
      ["Supplier", s.supplier_name],
      ["Supplier Code", s.supplier_code],
      ["Supplier Mobile", s.supplier_mobile],
      ["Allotment Number", s.assignment_number],
      ["Transaction ID", s.transaction_id],
      ["Outstanding Amount", formatCurrency(s.outstanding_amount)],
      ["Created On", formatDate(s.created_at)],
      ["Updated On", formatDate(s.updated_at)],
    ];
  }

  return [
    ["Vehicle Number", s.vehicle_number],
    ["Vehicle Model", s.vehicle_model],
    ["Display Code", s.display_code],
    ["RC Expiry", formatDate(s.rc_expiry_date)],
    ["Permit Expiry", formatDate(s.permit_expiry_date)],
    ["Fitness Expiry", formatDate(s.fitness_expiry_date)],
    ["Insurance Expiry", formatDate(s.insurance_expiry_date)],
    ["Tax Expiry", formatDate(s.tax_expiry_date)],
    ["Pollution Expiry", formatDate(s.pollution_expiry_date)],
    ["Police Sticker Expiry", formatDate(s.police_sticker_expiry_date)],
    [
      "City Police Permit Expiry",
      formatDate(s.city_police_permit_expiry_date),
    ],
  ];
}

function getFiles(type, item) {
  const s = getSource(item);

  if (type === "driver") {
    return [
      ["License", s.license_file_url],
      ["Aadhaar", s.adhaar_url],
      ["PAN", s.pancard_file_url],
      ["Bank Passbook", s.bank_passbook_photo_url],
    ];
  }

  if (type === "supplier") {
    return [
      ["Aadhaar", s.adhaar_url],
      ["License", s.license_file_url],
      ["PAN", s.pancard_file_url],
      ["Bank Passbook", s.bank_passbook_photo_url],
      ["Payment Receipt", s.payment_receipt_url],
      ["Agreement", s.agreement_document_url],
    ];
  }

  if (type === "assignment") {
    return [
      ["Allotment Document", s.allotment_document_photo],
      ["Front Photo", s.vehicle_photo_front],
      ["Back Photo", s.vehicle_photo_back],
      ["Left Photo", s.vehicle_photo_left],
      ["Right Photo", s.vehicle_photo_right],
    ];
  }

  if (type === "vehicle_documents") {
    return [
      ["RC File", s.rc_file_url],
      ["Permit File", s.permit_file_url],
      ["Fitness File", s.fitness_file_url],
      ["Insurance File", s.insurance_file_url],
      ["Tax File", s.tax_file_url],
      ["Pollution File", s.pollution_file_url],
      ["Police Sticker", s.police_sticker_file_url],
      ["City Police Permit", s.city_police_permit_file_url],
    ];
  }

  return [];
}

function ResultRow({ item, type, onOpen }) {
  const [expanded, setExpanded] = useState(false);

  const config = SECTION_CONFIG[type];
  const Icon = config.icon;
  const display = getDisplay(item, type);
  const details = getDetails(type, item).filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
  );
  const files = getFiles(type, item).filter(
    ([, value]) => value
  );

  return (
    <div
      className={`global-search-result-row global-search-result-row--${config.className} ${
        expanded ? "is-expanded" : ""
      }`}
    >
      <div className="global-search-result-main">
        <div className="global-search-result-avatar">
          {display.image ? (
            <img
              src={makeUrl(display.image)}
              alt={display.title}
            />
          ) : (
            <Icon />
          )}
        </div>

        <div className="global-search-result-copy">
          <div className="global-search-result-title">
            {display.title}
          </div>

          {display.subtitle && (
            <div className="global-search-result-subtitle">
              {display.subtitle}
            </div>
          )}

          {display.meta && (
            <div className="global-search-result-meta">
              {display.meta}
            </div>
          )}
        </div>

        <div className="global-search-result-actions">
          <button
            type="button"
            className="global-search-details-button"
            onClick={() =>
              setExpanded((value) => !value)
            }
          >
            <span>
              {expanded ? "Hide Details" : "Details"}
            </span>
            <FiChevronDown
              className={expanded ? "rotated" : ""}
            />
          </button>

          <button
            type="button"
            className="global-search-open-button"
            onClick={() => onOpen(item)}
            aria-label={`Open ${display.title}`}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="global-search-expanded">
          {details.length > 0 && (
            <div className="global-search-detail-grid">
              {details.map(([label, value]) => (
                <DetailItem
                  key={label}
                  label={label}
                  value={value}
                />
              ))}
            </div>
          )}

          {files.length > 0 && (
            <div className="global-search-files">
              <div className="global-search-files-heading">
                <FiFileText />
                <span>Documents</span>
              </div>

              <div className="global-search-file-list">
                {files.map(([label, value]) => (
                  <FileButton
                    key={label}
                    label={label}
                    value={value}
                  />
                ))}
              </div>
            </div>
          )}

          {details.length === 0 && files.length === 0 && (
            <div className="global-search-no-details">
              No additional details available for this record.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionCard({ type, items, onOpen }) {
  if (!items.length) {
    return null;
  }

  const config = SECTION_CONFIG[type];
  const Icon = config.icon;

  return (
    <section
      className={`global-search-section global-search-section--${config.className}`}
    >
      <div className="global-search-section-header">
        <div className="global-search-section-title">
          <div className="global-search-section-icon">
            <Icon />
          </div>

          <div>
            <h2>{config.title}</h2>
            <p>
              {items.length}{" "}
              {items.length === 1
                ? config.singular
                : config.title}
            </p>
          </div>
        </div>

        <span className="global-search-section-count">
          {items.length}
        </span>
      </div>

      <div className="global-search-section-list">
        {items.map((item, index) => (
          <ResultRow
            key={`${type}-${item?.id || index}`}
            item={item}
            type={type}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
}

export default function GlobalSearch() {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("q") || "").trim();
  }, [location.search]);

  const [results, setResults] = useState({
    vehicle: [],
    driver: [],
    supplier: [],
    assignment: [],
    staff: [],
    outstanding: [],
    vehicle_documents: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadResults() {
      if (query.length < 2) {
        setResults({
          vehicle: [],
          driver: [],
          supplier: [],
          assignment: [],
          staff: [],
          outstanding: [],
          vehicle_documents: [],
        });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await globalSearch(query);

        if (!cancelled) {
          setResults(normalizeResults(payload));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Unable to load search results."
          );
          setResults({
            vehicle: [],
            driver: [],
            supplier: [],
            assignment: [],
            staff: [],
            outstanding: [],
            vehicle_documents: [],
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadResults();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const totalResults = SECTION_ORDER.reduce(
    (sum, type) => sum + results[type].length,
    0
  );

  const visibleSections = SECTION_ORDER.filter(
    (type) => results[type].length > 0
  );

  const openResult = (item) => {
    if (item?.path) {
      navigate(item.path);
    }
  };

  return (
    <div className="admin-layout global-search-layout">
      <Sidebar />

      <div className="admin-main global-search-main">
        <Header />

        <main className="global-search-page">
         

          <div className="global-search-page-content">
            {loading && (
              <div className="global-search-state">
                <div className="global-search-spinner" />
                <span>
                  Searching for “{query}”...
                </span>
              </div>
            )}

            {!loading && error && (
              <div className="global-search-state global-search-state--error">
                <h3>Search failed</h3>
                <p>{error}</p>
              </div>
            )}

            {!loading &&
              !error &&
              query.length >= 2 &&
              visibleSections.length === 0 && (
                <div className="global-search-state">
                  <div className="global-search-state-icon">
                    <FiSearch />
                  </div>
                  <h3>No matching records</h3>
                  <p>
                    No matching vehicle, driver, supplier,
                    allotment, staff, outstanding, or vehicle
                    document was found.
                  </p>
                </div>
              )}

            {!loading &&
              !error &&
              query.length < 2 && (
                <div className="global-search-state">
                  <div className="global-search-state-icon">
                    <FiSearch />
                  </div>
                  <h3>Search from the header</h3>
                  <p>
                    Enter a vehicle, driver, supplier,
                    allotment, staff or document search in
                    the top header.
                  </p>
                </div>
              )}

            {!loading &&
              !error &&
              visibleSections.length > 0 && (
                <div className="global-search-section-grid">
                  {visibleSections.map((type) => (
                    <SectionCard
                      key={type}
                      type={type}
                      items={results[type]}
                      onOpen={openResult}
                    />
                  ))}
                </div>
              )}
          </div>
        </main>
      </div>
    </div>
  );
}
